// "Deployment" - first section of "IC Reporting", ported from reporting-hub/
// frontend/src/components/preflight/report/DeploymentChart.tsx (the fetch +
// filter controls that owned this in the original live in IcReportingShell.js
// per that file's own header comment - "Leading (left) = temporal controls
// ... trailing (right) = the scope filter"). Per time bucket, how much was
// invested vs. how much came back (principal + interest). The Calculate job
// stores a per-day series per scope (GET .../deployment-daily, fetched once
// per scope); frequency/date-window changes re-bucket it locally via
// deploymentBuckets.js buildDeploymentChart() - no request per change.
//
// Grouped bar: "Invested" as its own bar next to a stacked "Principal
// Returned"+"Interest Returned" bar, per bucket - reproduced in chart.js via
// two distinct `stack` groups on the same category axis (chart.js clusters
// separate stack groups side by side automatically), rather than the
// original's hand-rolled SVG bar-group math.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { Chart } from 'primereact/chart';
import { SelectButton } from 'primereact/selectbutton';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { BASE_OPTIONS, chartRemountKey } from '../../reporting/chart_theme';
import { formatCurrency, formatDate, titleCasePlatform, isoToDate, dateToIso, PLATFORM_ORDER } from '../calculation/calcFormat';
import { buildDeploymentChart } from './deploymentBuckets';
import errorMessage from '../../../helpers/errorMessage';

const FREQUENCY_OPTIONS = [
  { id: 'day', label: 'Daily' },
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' },
  { id: 'quarter', label: 'Quarterly' },
  { id: 'year', label: 'Yearly' },
];

const INVESTED_COLOR = '#0d366b';
const PRINCIPAL_COLOR = '#2a78d6';
const INTEREST_COLOR = '#6da7ec';

function formatCurrencyCompact(value) {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const millions = abs / 1_000_000;
    return `${sign}${(millions >= 10 ? millions.toFixed(0) : millions.toFixed(1)).replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    const thousands = abs / 1_000;
    return `${sign}${(thousands >= 10 ? thousands.toFixed(0) : thousands.toFixed(1)).replace(/\.0$/, '')}K`;
  }
  return `${sign}${abs.toFixed(0)}`;
}

function DeploymentChart({ data }) {
  const chartData = useMemo(
    () => ({
      labels: data.buckets.map((b) => b.label),
      datasets: [
        { label: 'Invested', backgroundColor: INVESTED_COLOR, data: data.buckets.map((b) => b.invested), stack: 'invested' },
        { label: 'Principal Returned', backgroundColor: PRINCIPAL_COLOR, data: data.buckets.map((b) => b.principal_returned), stack: 'returned' },
        { label: 'Interest Returned', backgroundColor: INTEREST_COLOR, data: data.buckets.map((b) => b.interest_returned), stack: 'returned' },
      ],
    }),
    [data]
  );

  const options = useMemo(
    () => ({
      ...BASE_OPTIONS,
      scales: {
        x: { ticks: { maxRotation: 90, minRotation: 0 } },
        y: { ticks: { callback: (v) => formatCurrencyCompact(v) } },
      },
      plugins: {
        legend: { position: 'bottom', labels: { boxWidth: 12 } },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => {
              const b = data.buckets[items[0]?.dataIndex ?? 0];
              return b ? `${b.label} (${formatDate(b.start)} - ${formatDate(b.end)})` : '';
            },
            label: (item) => `${item.dataset.label}: ${formatCurrency(item.raw)}`,
          },
        },
      },
    }),
    [data]
  );

  return (
    <div style={{ height: 380 }}>
      <Chart key={chartRemountKey(chartData)} type="bar" data={chartData} options={options} style={{ height: '100%', width: '100%' }} />
    </div>
  );
}

const DeploymentChartCard = ({ reportingId }) => {
  const [frequency, setFrequency] = useState('week');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [scope, setScope] = useState('fund');
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dailyCacheRef = useRef({ reportingId: null, byScope: {} });

  const rangeInvalid = !!(startDate && endDate && startDate > endDate);

  useEffect(() => {
    if (!reportingId) return;
    if (dailyCacheRef.current.reportingId !== reportingId) dailyCacheRef.current = { reportingId, byScope: {} };
    const cached = dailyCacheRef.current.byScope[scope];
    if (cached) {
      setDaily(cached);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDaily(null);
    const params = scope !== 'fund' ? { platform: scope } : {};
    axiosWithAuth
      .get(`/compartment-report/reportings/${reportingId}/deployment-daily`, { params })
      .then((res) => {
        if (!cancelled) {
          dailyCacheRef.current.byScope[scope] = res.data;
          setDaily(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[DeploymentChartCard] fetch error:', errorMessage(err));
          setError(errorMessage(err, 'Failed to load Deployment chart.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportingId, scope]);

  // Same result (and same 400-style messages) the old deployment chart endpoint gave.
  const { data, buildError } = useMemo(() => {
    if (!daily || rangeInvalid) return { data: null, buildError: null };
    try {
      return { data: buildDeploymentChart(daily, frequency, startDate, endDate), buildError: null };
    } catch (err) {
      return { data: null, buildError: err.message };
    }
  }, [daily, frequency, startDate, endDate, rangeInvalid]);

  return (
    <div className="cr-card cr-report-card">
      <h4 className="cr-card-title cr-card-title-block">Deployment</h4>
      <p className="cr-card-subtitle">Capital invested vs. principal + interest returned, over time.</p>

      <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div className="flex flex-wrap align-items-center gap-3">
          <SelectButton value={frequency} options={FREQUENCY_OPTIONS.map((o) => ({ label: o.label, value: o.id }))} onChange={(e) => (e.value ? setFrequency(e.value) : null)} />
          <div className="flex align-items-center gap-2">
            <Calendar value={isoToDate(startDate)} onChange={(e) => setStartDate(e.value ? dateToIso(e.value) : null)} placeholder="From" dateFormat="yy-mm-dd" showIcon />
            <span className="text-color-secondary">to</span>
            <Calendar value={isoToDate(endDate)} onChange={(e) => setEndDate(e.value ? dateToIso(e.value) : null)} placeholder="To" dateFormat="yy-mm-dd" showIcon />
            {(startDate || endDate) && (
              <Button
                label="All-time"
                className="p-button-link"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
              />
            )}
          </div>
        </div>
        <SelectButton
          value={scope}
          options={[{ label: 'Fund', value: 'fund' }, ...PLATFORM_ORDER.map((p) => ({ label: titleCasePlatform(p), value: p }))]}
          onChange={(e) => (e.value ? setScope(e.value) : null)}
        />
      </div>

      {rangeInvalid ? (
        <div className="ex-inline-error">&quot;From&quot; date must be on or before the &quot;to&quot; date.</div>
      ) : error || buildError ? (
        <div className="ex-inline-error">{error || buildError}</div>
      ) : loading || !data ? (
        <Skeleton height="380px" borderRadius="12px" />
      ) : data.window === null ? (
        <p className="text-color-secondary">No deployment activity found for this selection.</p>
      ) : (
        <div>
          <p className="text-color-secondary text-sm mb-2">
            Showing {formatDate(data.window.start)} - {formatDate(data.window.end)}
          </p>
          <DeploymentChart data={data} />
        </div>
      )}
    </div>
  );
};

export default DeploymentChartCard;
